import { ISettingArea, ISettingDescriptor } from './types';
import { AREAS as AREAS_LITERAL, SettingAreaKey } from './areas';
import { ORGANIZATION_SETTINGS } from './settings/organization';
import { SEARCH_SETTINGS } from './settings/search';
import { STOREFRONT_SETTINGS } from './settings/storefront';

export * from './types';
/** Widened for consumers: the literal tuple in areas.ts only exists to derive SettingAreaKey. */
export const AREAS: readonly ISettingArea[] = AREAS_LITERAL;
export type { SettingAreaKey } from './areas';

/**
 * Every setting in the app, keyed by a stable identifier.
 *
 * Add a new area file under ./settings and register it in both places below: the SettingId union
 * (compile-time checking of every <Setting id=...>) and the SETTINGS array (runtime lookup).
 */
export type SettingId =
    | (typeof ORGANIZATION_SETTINGS)[number]['id']
    | (typeof SEARCH_SETTINGS)[number]['id']
    | (typeof STOREFRONT_SETTINGS)[number]['id'];

export const SETTINGS: readonly ISettingDescriptor[] = [
    ...ORGANIZATION_SETTINGS,
    ...SEARCH_SETTINGS,
    ...STOREFRONT_SETTINGS,
];

/** Every `area` referenced by a setting, as literal types. */
type ReferencedAreaKey =
    | (typeof ORGANIZATION_SETTINGS)[number]['area']
    | (typeof SEARCH_SETTINGS)[number]['area']
    | (typeof STOREFRONT_SETTINGS)[number]['area'];

type ExpectNever<T extends never> = T;

/**
 * Fails to compile if a setting references an area that does not exist in AREAS. The error names
 * the offending key, e.g. `Type '"storefront.genral"' does not satisfy the constraint 'never'`.
 */
export type _EverySettingAreaExists = ExpectNever<Exclude<ReferencedAreaKey, SettingAreaKey>>;

/**
 * `dependsOn` is optional, so it is missing (not merely `undefined`) from most literal entries in
 * the union - indexing `['dependsOn']` directly the way `ReferencedAreaKey` indexes `['area']` above
 * does not typecheck for an absent property. This pulls it out with a conditional type instead,
 * which tolerates that, and drops entries that never set it via the `never` branch.
 */
type DependsOnOf<T> = T extends { dependsOn: infer D } ? D : never;

/** Every `dependsOn` referenced by a setting, as literal types. */
type ReferencedDependsOnId =
    | DependsOnOf<(typeof ORGANIZATION_SETTINGS)[number]>
    | DependsOnOf<(typeof SEARCH_SETTINGS)[number]>
    | DependsOnOf<(typeof STOREFRONT_SETTINGS)[number]>;

/**
 * Fails to compile if a setting's `dependsOn` names an id that is not registered - the same pattern
 * as `_EverySettingAreaExists` just above. `dependsOn` can't be typed against `SettingId` directly on
 * `ISettingDescriptor` (types.ts sits one level below this file, before `SettingId` exists), so the
 * check happens here instead, where `SettingId` is computed.
 */
export type _EveryDependsOnExists = ExpectNever<Exclude<ReferencedDependsOnId, SettingId>>;

const settingsById = new Map<string, ISettingDescriptor>();
const areasByKey = new Map<string, ISettingArea>();

for (const area of AREAS) {
    areasByKey.set(area.key, area);
}

for (const setting of SETTINGS) {
    if (settingsById.has(setting.id)) {
        // scripts/checkSettingIds.js is the authoritative check; this is the dev-time safety net.
        console.error(`Duplicate setting id in the registry: ${setting.id}`);
    }
    settingsById.set(setting.id, setting);
    setting.aliases?.forEach((alias) => settingsById.set(alias, setting));
}

/**
 * Settings that cannot be listed ahead of time.
 *
 * Some fields come from data rather than from code: an installed extension's schema, a storefront's
 * localization overrides. There is nothing to enumerate at build time, so their ids are synthesized
 * on demand from the property name. That keeps them linkable for anyone who knows the field exists -
 * a support case where the customer's configuration is in front of you - without pretending the
 * whole set can be published in advance.
 *
 * A link to a property the tenant has not configured resolves to the right screen and then reports
 * honestly that the field is not on the page, which is the truthful answer.
 */
const STYLE_SCOPE_AREAS: { [scope: string]: string } = {
    // Its own panel-keyed area, not storefront.pages.header - that area also carries the Header Bar
    // style picker's ids, which are edited in place rather than behind a panel.
    headerLinks: 'storefront.pages.headerLinks',
    navPod: 'storefront.pages.navPods.style',
    categoryPod: 'storefront.pages.categoryPods',
    productCard: 'storefront.pages.productCards',
    browseAll: 'storefront.pages.browseAll',
    searchPage: 'storefront.pages.search',
    bundlePage: 'storefront.pages.bundle',
    cartPage: 'storefront.pages.cart',
    checkoutPage: 'storefront.pages.checkout',
    productPage: 'storefront.pages.product',
    accountPersona: 'storefront.pages.headerFooter',
    loginPage: 'storefront.pages.login',
    notifications: 'storefront.pages.notifications',
    autocomplete: 'storefront.pages.searchSuggestions',
    namedStyle: 'storefront.pages.home',
};

/*
 * Both the Search and Product page editors' style rows sit behind SEVERAL topical panels, not one -
 * unlike navPod/categoryPod/browseAll etc, which each have exactly one panel, so the flat
 * STYLE_SCOPE_AREAS map above isn't enough: the element's own part decides which panel a link has to
 * open, per the "one area carries one panel" rule. See storefront.pages.product.* / .search.* in
 * areas.ts.
 */
const PRODUCT_PAGE_PANEL_BY_PART: { [part: string]: string } = {
    container: 'page', title: 'page',
    image: 'gallery', imageFrame: 'gallery', galleryStrip: 'gallery', thumb: 'gallery', thumbSelected: 'gallery', scrollButton: 'gallery',
    tags: 'chips', attributes: 'chips',
    description: 'description',
    priceBlock: 'purchase', msrp: 'purchase', quantity: 'purchase', addToCartButton: 'purchase', addToCartLabel: 'purchase',
    promoBadge: 'promotions', promoOriginalPrice: 'promotions', promoEndDate: 'promotions',
    linkedSection: 'linked', linkedHeading: 'linked', linkedRow: 'linked', linkedCard: 'linked', linkedRecommendedBadge: 'linked',
    addedTogetherSection: 'addedTogether', addedTogetherHeading: 'addedTogether', addedTogetherCard: 'addedTogether', addedTogetherScrollButton: 'addedTogether', addedTogetherScrollbar: 'addedTogether',
    moreLikeThisSection: 'moreLikeThis', moreLikeThisHeading: 'moreLikeThis', moreLikeThisCard: 'moreLikeThis', moreLikeThisScrollButton: 'moreLikeThis', moreLikeThisScrollbar: 'moreLikeThis',
};
const SEARCH_PAGE_PANEL_BY_PART: { [part: string]: string } = {
    container: 'container',
    paginatorContainer: 'paginator', paginatorCell: 'paginator',
    breadcrumbRow: 'breadcrumb', breadcrumbChip: 'breadcrumb',
    categoryChip: 'metaChips', manufacturerChip: 'metaChips',
    facetPanel: 'filters', facetHeading: 'filters',
    searchBar: 'searchBar',
};
interface IDynamicPattern {
    pattern: RegExp;
    build: (m: RegExpExecArray) => ISettingDescriptor;
    /**
     * A `condition` (and optionally `dependsOn`) that applies to every id this pattern matches - for
     * a family that is only ever reachable under one condition, so each `build()` does not have to
     * repeat it. A descriptor returned by `build()` can still set its own to override this.
     */
    condition?: string;
    dependsOn?: string;
}

const DYNAMIC_PATTERNS: IDynamicPattern[] = [
    {
        // extension.<sourceExtensionId>.<fieldName>
        pattern: /^extension\.([^.]+)\.(.+)$/,
        build: (m) => {
            const field = m[2];
            const built = {
                id: m[0],
                area: 'extensions.configuration',
                label: humanize(field),
                // Only offer the extensions this field actually belongs to.
                instanceFilter: m[1],
            };
            // The three CRM-sync toggles only render once the folio has a contact-import
            // sub-extension enabled. Field-scoped rather than a pattern-level condition (which
            // would claim it for every other extension.<source>.<field> id too).
            if (field === 'syncAllCompanies' || field === 'syncAllLocations' || field === 'syncAllContacts') {
                return { ...built, condition: 'Only appears when a contact-import sub-extension is enabled for this extension.' };
            }
            if (field === 'purchaseHistoryMonthsBack') {
                return { ...built, condition: 'Only appears when a purchase-history sub-extension is enabled for this extension.' };
            }
            return built;
        },
    },
    {
        /*
         * storefront.section.<editor>.<cssProperty>
         *
         * The section style editors are a thin surface over CSS: every control writes one property,
         * and the property name is already the stable identifier. Registering them individually
         * would mean enumerating the granular border and spacing variants by hand and keeping that
         * list in step with the editor, for no gain over the name the editor already uses.
         *
         * These need a section as well as a storefront, which a `/go/setting/` link cannot express
         * on its own - see buildAreaUrl. A link copied from the highlight while a section's editor
         * is open carries both and works; a generic one should point at the page editor instead.
         */
        pattern: /^storefront\.section\.(style|layout)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: `storefront.section.${m[1]}`,
            label: humanize(m[2]),
            description: 'Applies to the section whose editor is open.',
        }),
    },
    {
        /*
         * storefront.layoutElement.<elementId>.<field>
         *
         * One element's own props inside a layout grid — the header/footer builder and the mega-menu
         * panels. Elements carry an id, so no positional fallback.
         *
         * The tail may itself be dotted: a linkGroup element holds N links, addressed by position as
         * `…<elementId>.link2.url`. Position rather than link id, because links inside a group are
         * not guaranteed to have one — only links added since ids were introduced do, and a
         * `…link.undefined.url` id would resolve and then never match anything.
         */
        pattern: /^storefront\.layoutElement\.([^.]+)\.([A-Za-z][A-Za-z0-9.]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.headerFooter',
            // Label from the last segment; the middle names which link, which the description carries.
            label: humanize(m[2].split('.').pop() ?? m[2]),
            description: m[2].includes('.')
                ? `A property of link ${m[2].split('.')[0].replace('link', '')} in a layout link group.`
                : 'A property of one element in a layout grid.',
        }),
    },
    {
        /*
         * storefront.affiliationItem.<itemId>.<field>
         *
         * One logo in the affiliations band. Distinct from storefront.affiliations.<part>.<css>,
         * which styles the band, and from the registered band-level fields like Label and Gap.
         */
        pattern: /^storefront\.affiliationItem\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.affiliations',
            label: humanize(m[2]),
            description: 'A field on one affiliation logo.',
        }),
    },
    {
        /*
         * storefront.navPodItem.<podId>.<field>
         *
         * One nav pod's own content and destination, as opposed to storefront.navPod.<part>.<css>,
         * which is the styling shared by every pod on the storefront. Pods are created with an id,
         * so no positional fallback is needed.
         */
        pattern: /^storefront\.navPodItem\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => {
            const field = m[2];
            // Progressive disclosure: category only once a segment is picked, sub-category only
            // once a category is picked. Both controllers are themselves dynamic (per-pod) ids, not
            // ones registered in the static SETTINGS array, so there is no dependsOn to point at.
            if (field === 'category') {
                return {
                    id: m[0],
                    area: 'storefront.pages.navPods',
                    label: humanize(field),
                    description: 'A field on one navigation pod.',
                    condition: "Only appears once this pod's Segment is set.",
                };
            }
            if (field === 'subCategory') {
                return {
                    id: m[0],
                    area: 'storefront.pages.navPods',
                    label: humanize(field),
                    description: 'A field on one navigation pod.',
                    condition: "Only appears once this pod's Category is set.",
                };
            }
            return {
                id: m[0],
                area: 'storefront.pages.navPods',
                label: humanize(field),
                description: 'A field on one navigation pod.',
            };
        },
    },
    {
        /*
         * storefront.notificationBar.<barId>.<field>
         *
         * The scheduled bands above the header. A tenant builds the list, so nothing here can be
         * registered in advance, but each bar carries its own id — no position fallback needed.
         */
        pattern: /^storefront\.notificationBar\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.headerFooter',
            label: humanize(m[2]),
            description: 'A field on one notification bar.',
        }),
    },
    {
        /*
         * storefront.orderQuestion.<questionId>.<field>
         *
         * Per-storefront ad-hoc checkout questions, edited on the Order Questions tab. Distinct from
         * storefront.global.orderQuestion.* (org-wide questions edited on the equivalent global
         * screen) and storefront.quoteQuestion.* (a different question list entirely) - the same
         * shared editor renders all of these, so the family has to be in the id or a link to one
         * would resolve on top of another, the SMTP From Address trap again.
         */
        pattern: /^storefront\.orderQuestion\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.ordering.questions',
            label: humanize(m[2]),
            description: 'A field on one ad-hoc checkout question.',
            ...questionFieldCondition(m[2]),
        }),
    },
    {
        /*
         * storefront.global.orderQuestion.<questionId>.<field>
         *
         * The org-wide counterpart of storefront.orderQuestion.* - questions available to every
         * storefront, edited on Storefront · Ordering & Carts · Order Questions (all storefronts).
         */
        pattern: /^storefront\.global\.orderQuestion\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.global.ordering.questions',
            label: humanize(m[2]),
            description: 'A field on one organization-wide checkout question.',
            ...questionFieldCondition(m[2]),
        }),
    },
    {
        /*
         * storefront.quoteQuestion.<questionId>.<field>
         *
         * Quote request questions, a separate list from the checkout questions above even though
         * both use the same shared question editor - edited on the Quote Requests tab.
         */
        pattern: /^storefront\.quoteQuestion\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.ordering.quotes',
            label: humanize(m[2]),
            description: 'A field on one quote request question.',
            ...questionFieldCondition(m[2]),
        }),
    },
    {
        /*
         * storefront.bundleQuestion.<questionId>.<field>
         *
         * A question on one product bundle's Questions tab. Bundles have no route or list query of
         * their own (the editor is a modal hosted above the router), so unlike the other question
         * families this area carries no instanceType - a link needs an explicit ?instance=<bundleId>
         * rather than resolving through a picker. Produced by Copy Link from inside the open editor.
         */
        pattern: /^storefront\.bundleQuestion\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'bundles.questions',
            label: humanize(m[2]),
            description: 'A field on one product bundle question.',
            ...questionFieldCondition(m[2]),
        }),
    },
    {
        /*
         * storefront.headerLink.<linkId|index>.<field>
         * storefront.headerLink.<linkId|index>.style.<field>
         * storefront.headerLink.<linkId|index>.child.<childId|index>.<field>
         *
         * Custom header links are a list the tenant builds, so there is no fixed set to register.
         * Keyed by the link's own id where it has one, falling back to position — the same key the
         * editor already uses for a link's uploaded icon. A position-keyed id moves if the links are
         * reordered; an id-keyed one does not.
         *
         * A link's own submenu is a second, nested list (the dropdown items shown under it), so a
         * submenu item's id needs a second key inside the link's - `child.<childId>` - the same
         * position-or-id scheme one level down.
         */
        pattern: /^storefront\.headerLink\.([^.]+)\.(?:style\.([A-Za-z]+)|child\.([^.]+)\.([A-Za-z]+)|([A-Za-z]+))$/,
        build: (m) => {
            const childField = m[4];
            if (childField) {
                return {
                    id: m[0],
                    area: 'storefront.pages.header',
                    label: humanize(childField),
                    description: 'A field on one submenu item under a custom header link.',
                };
            }
            const plainField = m[5];
            if (plainField === 'panelFullWidth') {
                return {
                    id: m[0],
                    area: 'storefront.pages.header',
                    label: humanize(plainField),
                    description: 'A field on one custom header link.',
                    condition: "Only appears once this header link has a mega-menu panel set up.",
                };
            }
            return {
                id: m[0],
                area: 'storefront.pages.header',
                label: humanize(m[2] ?? m[5]),
                description: 'A field on one custom header link.',
            };
        },
    },
    {
        /*
         * storefront.footerLink.<linkId|index>.<field>
         *
         * Custom footer links are a list the tenant builds, so there is no fixed set to register.
         * Keyed by the link's own id where it has one, falling back to position — the same scheme
         * storefront.headerLink uses.
         */
        pattern: /^storefront\.footerLink\.([^.]+)\.([A-Za-z]+)$/,
        build: (m) => {
            const field = m[2];
            if (field === 'url') {
                return {
                    id: m[0],
                    area: 'storefront.pages.headerFooter',
                    label: humanize(field),
                    description: 'A field on one custom footer link.',
                    condition: "Only appears when this footer link's Section Header toggle is off.",
                };
            }
            return {
                id: m[0],
                area: 'storefront.pages.headerFooter',
                label: humanize(field),
                description: 'A field on one custom footer link.',
            };
        },
    },
    {
        /*
         * storefront.footer.social.<platform>
         *
         * One social network's handle or URL in the footer. The platform list is fixed by the
         * editor, not tenant data, but stays dynamic because one call site renders all of them from
         * a config array.
         */
        pattern: /^storefront\.footer\.social\.([A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.headerFooter',
            label: `${humanize(m[1])} handle`,
            description: "The footer social icon's handle or URL.",
        }),
    },
    {
        /*
         * extensionFieldMap.<sourceExtensionId>.<parentField>.<mappedKey>
         *
         * A mapped field inside an extension's dynamic field map. Which keys exist comes from the
         * extension's own schema, so none of these can be registered ahead of time — but a support
         * conversation that already knows a customer has one mapped can link straight to it.
         */
        pattern: /^extensionFieldMap\.([^.]+)\.([^.]+)\.(.+)$/,
        build: (m) => ({
            id: m[0],
            area: 'extensions.configuration',
            label: humanize(m[3]),
            description: `Mapped under ${humanize(m[2])}.`,
            instanceFilter: m[1],
        }),
    },
    {
        /*
         * vendors.folioMapping.<folioId>
         *
         * The vendor panel renders one mapping row per connected CRM folio, so there is no fixed set
         * of ids to register — the folio id is the only thing that identifies a row.
         */
        pattern: /^vendors\.folioMapping\.([A-Za-z0-9_-]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'vendors.details',
            label: 'CRM Vendor Mapping',
            description: 'The mapping row for one connected CRM.',
        }),
    },
    {
        /*
         * storefront.page.<pageId>.<field>
         *
         * A custom page's own title, slug and enabled state, in the Manage Custom Pages panel. Pages
         * are created with an id, so no positional fallback is needed. The "Add a page" form stays
         * unregistered - there is nothing yet to link to until a page exists.
         */
        pattern: /^storefront\.page\.([^.]+)\.(title|slug|enabled)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.managePages',
            label: humanize(m[2]),
            description: 'A field on one custom page, in the Manage Custom Pages panel.',
        }),
    },
    {
        /*
         * storefront.fulfillmentVendor.<sourceVendor>.<field>
         *
         * The Fulfillment tab's per-vendor settings, edited one vendor at a time behind a panel.
         * Vendors are keyed by their sourceVendor, the same key the rest of the app passes around
         * for a vendor, and there is no fixed set to register since the row list comes from the
         * catalog's vendor data.
         */
        pattern: /^storefront\.fulfillmentVendor\.([^.]+)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.global.products.fulfillment',
            label: humanize(m[2]),
            description: 'A fulfillment setting for one vendor.',
        }),
    },
    {
        /*
         * storefront.smtp.<field>
         *
         * The From Address / From Name pair renders once per email kind (login, order, quote,
         * prepared cart), so the id has to name the field it writes rather than the pair.
         */
        pattern: /^storefront\.smtp\.(from[A-Za-z]+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.global.integrations.email',
            label: humanize(m[1]),
        }),
    },
    {
        /*
         * storefront.sectionStyle.<sectionId>.<part>.<cssProperty>
         *
         * One CSS property inside a style group — the raw StyleProps rows. `…<id>.header` is the
         * group, `…<id>.header.fontSize` is a row in it. Declared before the group pattern so the
         * longer form wins; patterns are tried in order.
         */
        pattern: /^storefront\.sectionStyle\.([^.]+)\.(section|header|footer|productCards|button)\.([A-Za-z]+)$/,
        build: (m) => {
            const part = m[2];
            const built = {
                id: m[0],
                area: 'storefront.section.appearance',
                label: humanize(m[3]),
                description: `A style property of one section's ${SECTION_PART_LABELS[part] ?? humanize(part)}.`,
            };
            // Footer / Product Cards groups only render for a product-listing section type; Button
            // styling only exists on a Rich Text section's optional CTA.
            if (part === 'footer' || part === 'productCards') {
                return { ...built, condition: 'Only appears when Section Type is a product listing type.' };
            }
            if (part === 'button') {
                return { ...built, condition: 'Only appears when Section Type is Rich Text.' };
            }
            return built;
        },
    },
    {
        /*
         * storefront.sectionStyle.<sectionId>.<part>
         *
         * The page editor renders every section's editor at once, so a style group's id has to name
         * its section - a shared id would highlight whichever section happened to be first, the same
         * trap the SMTP From fields fell into.
         */
        pattern: /^storefront\.sectionStyle\.([^.]+)\.(section|header|footer|productCards)$/,
        build: (m) => {
            const part = m[2];
            const built = {
                id: m[0],
                area: 'storefront.section.appearance',
                label: SECTION_PART_LABELS[part] ?? humanize(part),
                description: 'Styling for one section, in the page editor.',
            };
            if (part === 'footer' || part === 'productCards') {
                return { ...built, condition: 'Only appears when Section Type is a product listing type.' };
            }
            return built;
        },
    },
    {
        /*
         * storefront.sectionField.<sectionId>.<field>
         *
         * A section's own settings (type, width, header visibility, ...) on the Basic tab, as
         * opposed to its styling. Section-scoped for the same reason.
         *
         * Many of these only render for a particular Section Type, or (verticalAlignment,
         * collapsible) for a particular Width/Hide-header value — SECTION_FIELD_CONDITIONS carries
         * that condition sentence per field. Every controller here (sectionType, width, hideHeader,
         * ...) is itself a per-section dynamic id rather than one registered in the static SETTINGS
         * array, so none of these set `dependsOn`.
         */
        pattern: /^storefront.sectionField.([^.]+).([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => {
            const field = m[2];
            const condition = SECTION_FIELD_CONDITIONS[field];
            return {
                id: m[0],
                area: 'storefront.section.appearance',
                label: humanize(field),
                description: 'A setting on one section, in the page editor.',
                ...(condition ? { condition } : {}),
            };
        },
    },
    {
        /*
         * storefront.header.<styleKey>
         *
         * Header bar entries in the in-place style picker. Keyed off the style property because
         * the picker's entry labels repeat across editors, so the label is not an identifier.
         */
        pattern: /^storefront\.header\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.header',
            label: humanize(m[1]),
        }),
    },
    {
        /*
         * storefront.style.<styleProperty>
         *
         * Storefront-wide colours and fonts. Several editors write the same property - the page
         * style bar and the header bar both set headerFont - so the id names the PROPERTY, and
         * every surface that edits it uses the same one.
         */
        pattern: /^storefront\.style\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.styles',
            label: humanize(m[1]),
        }),
    },
    {
        /*
         * storefront.banner.<bannerId>.<field>
         *
         * One slide's own settings — alt text, link, the call-to-action, the overlay's position.
         * Banner-scoped because they belong to a slide rather than to the carousel, and because
         * the id has to survive slides being reordered, which index-based naming would not.
         *
         * The panel edits the selected slide, so a link also has to say which one; that is what
         * Copy link in the highlight produces.
         */
        pattern: /^storefront\.banner\.([^.]+)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.banners',
            label: humanize(m[2]),
            description: 'A setting on one banner slide.',
        }),
    },
    {
        /*
         * storefront.affiliations.<part>.<cssProperty>
         *
         * The affiliations strip styles five parts (band, pod, image, label, description) through
         * the shared ElementPropertyRows. The prefix names the part - the rows themselves cannot,
         * since they render for whatever dict the caller hands them.
         */
        pattern: /^storefront\.affiliations\.(band|pod|image|label|description)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.affiliations',
            label: `${humanize(m[2])} (${m[1]})`,
        }),
    },
    {
        /*
         * storefront.<scope>.<part>.<cssProperty>
         *
         * The shared ElementPropertyRows renders the same twelve rows for whatever style dict it
         * is handed, so the caller supplies a prefix naming the element — `storefront.navPod.pod`,
         * `storefront.searchPage.facetPanel`. The scope decides which page editor the link opens.
         */
        pattern: /^storefront\.(navPod|categoryPod|productCard|browseAll|searchPage|bundlePage|cartPage|checkoutPage|productPage|accountPersona|loginPage|notifications|autocomplete|namedStyle|headerLinks)\.([A-Za-z][A-Za-z0-9]*)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => {
            const scope = m[1];
            // productPage and searchPage each have several panels, so the part (not just the scope)
            // decides which one to open; every other scope has exactly one panel/area.
            if (scope === 'productPage') {
                const panel = PRODUCT_PAGE_PANEL_BY_PART[m[2]];
                return {
                    id: m[0],
                    area: panel ? `storefront.pages.product.${panel}` : 'storefront.pages.product',
                    label: `${humanize(m[3])} (${humanize(m[2])})`,
                };
            }
            if (scope === 'searchPage') {
                const panel = SEARCH_PAGE_PANEL_BY_PART[m[2]];
                return {
                    id: m[0],
                    area: panel ? `storefront.pages.search.${panel}` : 'storefront.pages.search',
                    label: `${humanize(m[3])} (${humanize(m[2])})`,
                };
            }
            return {
                id: m[0],
                area: STYLE_SCOPE_AREAS[scope] ?? 'storefront.pages.home',
                label: `${humanize(m[3])} (${humanize(m[2])})`,
            };
        },
    },
    {
        /*
         * storefront.bannerStyle.<bannerId>.<part>.<cssProperty>
         *
         * The overlay's title, subtitle, button and backdrop box styling. Per slide, like the
         * slide fields, so the banner id sits in the id rather than a flat scope.
         */
        pattern: /^storefront\.bannerStyle\.([^.]+)\.(title|subtitle|button|box)\.([A-Za-z][A-Za-z0-9]*)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.pages.banners',
            label: `${humanize(m[3])} (overlay ${m[2]})`,
            description: 'Styling on one banner slide\'s overlay.',
        }),
    },
    {
        // storefront.localization.<localizationKey>
        pattern: /^storefront\.localization\.(.+)$/,
        build: (m) => ({
            id: m[0],
            area: 'storefront.localization',
            // The raw key, not a prettified version: it is what the editor shows and what anyone
            // looking at a storefront's overrides is reading off the screen.
            label: m[1],
            description: 'A localization override. Only listed on the screen once the storefront sets it.',
        }),
    },
];

/**
 * `condition` for the shared question-editor's allowMultiSelect field, shared by all four
 * question-family dynamic patterns (orderQuestion, global.orderQuestion, quoteQuestion,
 * bundleQuestion) rather than repeated in each one's build().
 */
const questionFieldCondition = (field: string): { condition?: string } =>
    field === 'allowMultiSelect'
        ? { condition: 'Only appears once this question has more than one answer.' }
        : {};

const SECTION_PART_LABELS: { [part: string]: string } = {
    section: 'Section styling',
    header: 'Header styling',
    footer: 'Footer styling',
    productCards: 'Product Cards styling',
    button: 'Button styling',
};

/**
 * `condition` sentence per storefront.sectionField.<id>.<field>, for the fields that only render
 * for a particular Section Type (or, for verticalAlignment/collapsible, a particular Width/Hide
 * header value). Fields not listed here (sectionType, width, hideHeader, ...) always render once
 * the section exists, so they carry no condition.
 */
const SECTION_FIELD_CONDITIONS: { [field: string]: string } = {
    verticalAlignment: 'Only appears when Width is a fractional width, not Full.',
    collapsible: 'Only appears when Hide header on storefront is off.',
    maxProducts: 'Only appears when Section Type is a product listing type.',
    sideScroll: 'Only appears when Section Type is a product listing type.',
    columns: 'Only appears when Section Type is Rich Text.',
    buttonLabel: 'Only appears when Section Type is Rich Text.',
    buttonUrl: 'Only appears when Section Type is Rich Text.',
    buttonPlacement: 'Only appears when Section Type is Rich Text.',
    buttonAlignment: 'Only appears when Section Type is Rich Text.',
    videoUrl: 'Only appears when Section Type is Video.',
    bannerHeight: 'Only appears when Section Type is Banner.',
    bannerImageFit: 'Only appears when Section Type is Banner.',
    bannerFullBleed: 'Only appears when Section Type is Banner.',
    bannerBackground: 'Only appears when Section Type is Banner.',
    dividerLineStyle: 'Only appears when Section Type is Divider.',
    dividerWidth: 'Only appears when Section Type is Divider.',
    dividerSpacing: 'Only appears when Section Type is Divider.',
    dividerThickness: 'Only appears when Section Type is Divider and Line style is not None.',
    dividerColor: 'Only appears when Section Type is Divider and Line style is not None.',
    blurb: 'Only appears when Section Type is Single Product.',
    ctaButtonText: 'Only appears when Section Type is Single Product.',
    manufacturers: 'Only appears when Section Type is Categories/Segments.',
    segment: 'Only appears when Section Type is Categories/Segments.',
    categories: 'Only appears when Section Type is Categories/Segments.',
    subCategories: 'Only appears when Section Type is Categories/Segments and at least one category is selected.',
    promotions: 'Only appears when Section Type is Selected Promotions.',
    distributorPromotions: 'Only appears when Section Type is Selected Promotions.',
    recommendationMaxProducts: 'Only appears when Section Type is Recommended For You.',
    recencyHalfLife: 'Only appears when Section Type is Recommended For You.',
    excludePurchased: 'Only appears when Section Type is Recommended For You.',
};

/** "companyIdentifier" -> "Company Identifier", so an unresolved field still reads as a label. */
const humanize = (field: string): string =>
    field
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (c) => c.toUpperCase());

const resolveDynamicSetting = (id: string): ISettingDescriptor | undefined => {
    for (const { pattern, build, condition, dependsOn } of DYNAMIC_PATTERNS) {
        const match = pattern.exec(id);
        if (!match) continue;
        const built = build(match);
        if (condition === undefined && dependsOn === undefined) return built;
        return { ...built, condition: built.condition ?? condition, dependsOn: built.dependsOn ?? dependsOn };
    }
    return undefined;
};

/** Look up a setting by its id or by any of its aliases. */
export const findSetting = (id?: string): ISettingDescriptor | undefined =>
    id ? settingsById.get(id) ?? resolveDynamicSetting(id) : undefined;

export const findArea = (key?: string): ISettingArea | undefined => (key ? areasByKey.get(key) : undefined);

/** The area a setting lives in, resolved through the setting's `area` key. */
export const findAreaForSetting = (id?: string): ISettingArea | undefined => findArea(findSetting(id)?.area);

export const getSettingsForArea = (areaKey: string): ISettingDescriptor[] =>
    SETTINGS.filter((s) => s.area === areaKey);

/**
 * Build the concrete URL a setting lives at.
 *
 * Returns undefined when the area needs an instance and none was supplied, so callers can prompt
 * for one rather than navigating somewhere wrong.
 */
export const buildSettingUrl = (
    settingId: string,
    options?: { instanceId?: string; includeFocus?: boolean }
): string | undefined => {
    const setting = findSetting(settingId);
    const area = findArea(setting?.area);
    if (!area) return undefined;
    const url = buildAreaUrl(area, options?.instanceId);
    if (!url) return undefined;
    if (options?.includeFocus === false || !setting) return url;
    const [path, search] = url.split('?');
    const params = new URLSearchParams(search);
    // The canonical id, not the alias the caller may have arrived with.
    params.set('focus', setting.id);
    return `${path}?${params.toString()}`;
};

/** Build the URL for an area, including its tab/panel/expand state. */
export const buildAreaUrl = (area: ISettingArea, instanceId?: string): string | undefined => {
    if (area.instanceType && !instanceId) return undefined;
    const path = instanceId ? area.route.replace(':instance', encodeURIComponent(instanceId)) : area.route;
    const params = new URLSearchParams();
    if (area.tab) params.set('tab', area.tab);
    if (area.panel) {
        params.set('panel', area.panel.key);
        if (area.panel.tab) params.set('panelTab', area.panel.tab);
        // Panel-based screens keep the instance in the query rather than the path.
        if (instanceId && !area.route.includes(':instance')) params.set('instance', instanceId);
    }
    if (area.expand) params.set('expand', area.expand);
    const search = params.toString();
    return search ? `${path}?${search}` : path;
};

/** The `/go/setting/...` link to hand to documentation. */
export const buildDeepLink = (settingId: string, options?: { instanceId?: string; origin?: string }): string => {
    const origin = options?.origin ?? window.location.origin;
    const query = options?.instanceId ? `?instance=${encodeURIComponent(options.instanceId)}` : '';
    return `${origin}/go/setting/${encodeURIComponent(settingId)}${query}`;
};

/**
 * The `/go/area/...` link for a whole screen.
 *
 * Some things are better documented as a place than as a field - the page editors and the
 * localization overrides configure content that varies per storefront and per section, so there is
 * no fixed field to point at, but "this is the screen" is still worth linking.
 */
export const buildAreaDeepLink = (areaKey: string, options?: { instanceId?: string; origin?: string }): string => {
    const origin = options?.origin ?? window.location.origin;
    const query = options?.instanceId ? `?instance=${encodeURIComponent(options.instanceId)}` : '';
    return `${origin}/go/area/${encodeURIComponent(areaKey)}${query}`;
};

